/**
 * Main App component with routing
 *
 * Requirements: 16.1
 */

import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { QueryStatusBanner } from './components/QueryStatusBanner';
import { LoadingState } from './components/ui';
import { useAuthStore } from './store/authStore';
import { useSettingsStore } from './store/settingsStore';
import { accountingEnabled } from './config/features';

const LoginPage = lazy(() => import('./pages/Login').then((module) => ({ default: module.LoginPage })));
const TelegramCallbackPage = lazy(() => import('./pages/TelegramCallback').then((module) => ({ default: module.TelegramCallbackPage })));
const HomePage = lazy(() => import('./pages/Home').then((module) => ({ default: module.HomePage })));
const PortfolioPage = lazy(() => import('./pages/Portfolio').then((module) => ({ default: module.PortfolioPage })));
const ExpensesPage = lazy(() => import('./pages/Expenses').then((module) => ({ default: module.ExpensesPage })));
const AccountingPage = accountingEnabled ? lazy(() => import('./pages/Accounting').then((module) => ({ default: module.AccountingPage }))) : null;
const SubscriptionsPage = lazy(() => import('./pages/Subscriptions').then((module) => ({ default: module.SubscriptionsPage })));
const SettingsPage = lazy(() => import('./pages/Settings').then((module) => ({ default: module.SettingsPage })));
const CurrenciesPage = lazy(() => import('./pages/Currencies').then((module) => ({ default: module.CurrenciesPage })));
const ConnectionsPage = lazy(() => import('./pages/connections/Connections').then((module) => ({ default: module.ConnectionsPage })));
const GooglePage = lazy(() => import('./pages/connections/Google').then((module) => ({ default: module.GooglePage })));
const ExchangesPage = lazy(() => import('./pages/connections/Exchanges').then((module) => ({ default: module.ExchangesPage })));
const AddExchangePage = lazy(() => import('./pages/connections/AddExchange').then((module) => ({ default: module.AddExchangePage })));
const GooglePlayPage = lazy(() => import('./pages/connections/GooglePlay').then((module) => ({ default: module.GooglePlayPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      staleTime: 30_000,
    },
  },
});

function RouteFallback() {
  return <LoadingState message="Loading…" />;
}

function AppContent() {
  const initialize = useAuthStore((s) => s.initialize);
  const theme = useSettingsStore((s) => s.theme);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#141820' : '#F4F5F8');
  }, [theme]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/telegram/callback" element={<TelegramCallbackPage />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<HomePage />} />
        <Route path="/portfolio" element={<PortfolioPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/expenses" element={<ExpensesPage />} />
        {AccountingPage && <Route path="/accounting" element={<AccountingPage />} />}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/currencies" element={<CurrenciesPage />} />
        <Route path="/settings/connections" element={<ConnectionsPage />} />
        <Route path="/settings/connections/google" element={<GooglePage />} />
        <Route path="/settings/connections/exchanges" element={<ExchangesPage />} />
        <Route path="/settings/connections/exchanges/add" element={<AddExchangePage />} />
        <Route path="/settings/connections/google-play" element={<GooglePlayPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary>
        <BrowserRouter>
          <QueryStatusBanner />
          <Suspense fallback={<RouteFallback />}>
            <AppContent />
          </Suspense>
        </BrowserRouter>
      </AppErrorBoundary>
    </QueryClientProvider>
  );
}
